/**
 * WalletProviderGate
 *
 * Resolves the active chain's wallet backend (`getWalletProviderKind()`, driven by
 * REACT_APP_CHAIN_ID) and mounts the right SDK provider around the app:
 *
 *   - WEB3REACT (default): fast passthrough — no SDK, no lazy load, no Suspense.
 *     Guarantees existing chains behave exactly as before.
 *   - PRIVY / PARA / DOGEOS: validates required env, lazy-loads the adapter chunk
 *     (so other SDKs aren't bundled), and mounts `<adapter.Provider>` +
 *     `WalletAdapterPublisher` so the controller is available to the whole tree.
 *
 * An ErrorBoundary wraps the SDK provider so an init crash renders a visible
 * message instead of a white screen. Missing env is caught *before* mounting the
 * SDK, which is the more common failure and produces a clearer error.
 */
import React, { Suspense } from "react";
import { COLORS } from "../../constants/design/colors";
import { getChainIdFromEnv, getWalletProviderKind } from "../../config/chains";
import { ChainIdEnum } from "../../enums/chain";
import {
  WALLET_PROVIDER_REQUIRED_ENV,
  WalletProviderKind,
} from "../../enums/walletProvider";
import { loadWalletAdapter } from "../../utils/wallet/providers/registry";
import { WalletAdapterPublisher } from "../../utils/wallet/providers/context";

interface GateProps {
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Fallback UI (kept minimal + self-contained; the gate wraps the whole app)
// ---------------------------------------------------------------------------

const fullScreen: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  // Match the app's brand background so this reads as the app's own splash, not a
  // separate screen. REACT_APP_BRAND_THEME_COLOR is the per-chain brand color.
  background: process.env.REACT_APP_BRAND_THEME_COLOR || "#000000",
  color: "#e6e6f0",
  fontFamily: "system-ui, sans-serif",
  padding: 24,
  textAlign: "center",
  zIndex: 9999,
};

// Same brand mark the pre-mount HTML splash renders (public/index.html), so the
// static splash and this React fallback show an identical logo — no swap flash.
const BRAND_LOGO = `/assets/logo/${
  process.env.REACT_APP_BRAND_LOGO_FOLDER || "marginx"
}/favicon.svg`;

/**
 * Brand splash shown while the SDK wallet provider chunk loads (SDK chains only).
 *
 * Deliberately mirrors the pre-mount HTML splash in public/index.html (#app-splash):
 * the brand mark with cyan "sonar" ripples radiating outward plus a subtle
 * breathe. React's first commit swaps the static splash for this one, so the user
 * perceives a single continuous loader with no jump.
 */
function WalletProviderLoading() {
  const ring = (delay: number): React.CSSProperties => ({
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: `1.5px solid ${COLORS.highlight.one}`,
    willChange: "transform, opacity",
    animation: `wpg-ripple 2.1s cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}s infinite`,
  });
  return (
    <div style={fullScreen} aria-busy="true" aria-label="Loading">
      <style>
        {`@keyframes wpg-ripple{0%{transform:scale(1);opacity:.5}100%{transform:scale(2.9);opacity:0}}` +
          `@keyframes wpg-breathe{0%,100%{transform:scale(.97)}50%{transform:scale(1.03)}}` +
          `@media (prefers-reduced-motion: reduce){.wpg-ring{display:none}.wpg-logo{animation:none}}`}
      </style>
      <div
        style={{
          position: "relative",
          display: "grid",
          placeItems: "center",
          width: 78,
          height: 78,
        }}
      >
        <span className="wpg-ring" style={ring(0)} />
        <span className="wpg-ring" style={ring(0.7)} />
        <span className="wpg-ring" style={ring(1.4)} />
        <img
          className="wpg-logo"
          src={BRAND_LOGO}
          alt=""
          style={{
            width: 78,
            height: 78,
            objectFit: "contain",
            position: "relative",
            zIndex: 2,
            willChange: "transform",
            animation: "wpg-breathe 2s ease-in-out infinite",
          }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      </div>
    </div>
  );
}

function WalletConfigError({
  kind,
  missing,
}: {
  kind: WalletProviderKind;
  missing: string[];
}) {
  return (
    <div style={fullScreen} role="alert">
      <div>
        <h1 style={{ fontSize: 20, marginBottom: 12 }}>
          Wallet not configured
        </h1>
        <p style={{ opacity: 0.8, maxWidth: 460 }}>
          The <strong>{kind}</strong> wallet backend for this deployment is
          missing required environment{" "}
          {missing.length === 1 ? "variable" : "variables"}:{" "}
          <code>{missing.join(", ")}</code>. Set{" "}
          {missing.length === 1 ? "it" : "them"} and rebuild.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error boundary around the SDK provider
// ---------------------------------------------------------------------------

class WalletProviderErrorBoundary extends React.Component<
  { kind: WalletProviderKind; children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { kind: WalletProviderKind; children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error(
      `[WalletProviderGate] ${this.props.kind} provider failed to initialize`,
      error,
      info
    );
  }

  render() {
    if (this.state.error) {
      return (
        <div style={fullScreen} role="alert">
          <div>
            <h1 style={{ fontSize: 20, marginBottom: 12 }}>
              Wallet unavailable
            </h1>
            <p style={{ opacity: 0.8, maxWidth: 460 }}>
              The {this.props.kind} wallet provider failed to start. Please
              reload; if this persists, contact support.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Lazy adapter mount (one lazy component per kind, cached)
// ---------------------------------------------------------------------------

type MountComponent = React.LazyExoticComponent<
  React.ComponentType<{ chainId: ChainIdEnum; children: React.ReactNode }>
>;

const mountCache = new Map<WalletProviderKind, MountComponent>();

function getAdapterMount(kind: WalletProviderKind): MountComponent {
  const cached = mountCache.get(kind);
  if (cached) return cached;

  const lazyMount = React.lazy(async () => {
    const { default: adapter } = await loadWalletAdapter(kind);
    const Mount: React.FC<{ chainId: ChainIdEnum; children: React.ReactNode }> =
      ({ chainId, children }) => (
        <adapter.Provider chainId={chainId}>
          <WalletAdapterPublisher adapter={adapter}>
            {children}
          </WalletAdapterPublisher>
        </adapter.Provider>
      );
    return { default: Mount };
  });

  mountCache.set(kind, lazyMount);
  return lazyMount;
}

const getMissingEnv = (kind: WalletProviderKind): string[] =>
  (WALLET_PROVIDER_REQUIRED_ENV[kind] ?? []).filter(
    (name) => !process.env[name]
  );

export function WalletProviderGate({ children }: GateProps) {
  const kind = getWalletProviderKind();

  // Fast path: default web3-react stack — identical to pre-change behavior.
  if (kind === WalletProviderKind.WEB3REACT) {
    return <>{children}</>;
  }

  const missing = getMissingEnv(kind);
  if (missing.length > 0) {
    return <WalletConfigError kind={kind} missing={missing} />;
  }

  const AdapterMount = getAdapterMount(kind);
  const chainId = getChainIdFromEnv();

  return (
    <WalletProviderErrorBoundary kind={kind}>
      <Suspense fallback={<WalletProviderLoading />}>
        <AdapterMount chainId={chainId}>{children}</AdapterMount>
      </Suspense>
    </WalletProviderErrorBoundary>
  );
}

export default WalletProviderGate;
